/**
 * KAN-303: голосовой пакет в кабинете — остаток и история списаний.
 *
 * Показываем и НЕтарифицируемые звонки тоже: клиент должен видеть, что работа
 * шла и что за неё с него не взяли. Это снимает половину вопросов к счёту ещё
 * до того, как они возникнут.
 *
 * KAN-341: переехало из Настроек в раздел «Биллинг». Витрина тарифов отсюда
 * УБРАНА: она живёт на вкладке «Тариф», а две витрины на соседних вкладках —
 * это два места, где можно купить одно и то же, и одно из них однажды отстанет.
 */
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { voiceBillingApi } from '@/services/api/voiceBilling';
import { VOICE_STATE_LABEL, type VoiceBalanceState } from '@/types/voiceBilling';

/** Цвет состояния. Семантика, а не акцент: красный значит «звонки не идут». */
const STATE_STYLE: Record<VoiceBalanceState, string> = {
  ok: 'bg-green-100 text-green-700',
  low: 'bg-amber-100 text-amber-800',
  grace: 'bg-amber-100 text-amber-800',
  stopped: 'bg-red-100 text-red-700',
};

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

/** Дней до конца периода. Отрицательное — срок уже прошёл. */
function daysLeft(periodEnd: string): number {
  const ms = new Date(periodEnd).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

function formatPeriodEnd(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  });
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function VoicePackage() {
  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['voice-balance'],
    queryFn: voiceBillingApi.getBalance,
  });

  const { data: charges, isLoading: chargesLoading } = useQuery({
    queryKey: ['voice-charges'],
    queryFn: () => voiceBillingApi.getCharges(),
  });

  if (balanceLoading) {
    return (
      <div data-testid="voice-package-loading" className="text-sm text-gray-500">
        Загружаем данные пакета…
      </div>
    );
  }

  if (!balance) {
    return (
      <div data-testid="voice-package-error" className="text-sm text-gray-500">
        Не удалось получить состояние пакета. Обновите страницу.
      </div>
    );
  }

  // Доля заполнения. Ограничиваем сотней, чтобы полоса не выезжала за карточку
  // при работе в долг — сам перерасход показан числом рядом.
  const usedPercent =
    balance.included_minutes > 0
      ? Math.min(100, Math.round((balance.used_minutes / balance.included_minutes) * 100))
      : 0;
  const inDebt = balance.remaining_minutes < 0;

  return (
    <div data-testid="voice-package" className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Пакет минут</CardTitle>
          <span
            data-testid="voice-state-badge"
            className={`rounded-full px-3 py-1 text-xs font-medium ${STATE_STYLE[balance.state]}`}>
            {VOICE_STATE_LABEL[balance.state]}
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          {!balance.includes_voice ? (
            <p className="text-sm text-gray-600" data-testid="voice-not-included-note">
              Звонки не входят в ваш тариф. Сметы и текстовые боты работают как обычно;
              чтобы подключить голосовых, выберите тариф со звонками.
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-semibold tabular-nums" data-testid="voice-remaining">
                  {inDebt
                    ? `−${Math.abs(balance.remaining_minutes)} мин`
                    : `${balance.remaining_minutes} мин`}
                </span>
                <span className="text-sm text-gray-500">
                  {balance.plan_name ? `${balance.plan_name} · ` : ''}
                  {balance.used_minutes} из {balance.included_minutes} мин
                </span>
              </div>

              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full ${inDebt ? 'bg-amber-500' : 'bg-blue-600'}`}
                  style={{ width: `${usedPercent}%` }}
                />
              </div>

              {inDebt && (
                <p className="text-sm text-amber-800" data-testid="voice-grace-note">
                  Пакет выбран, обзвон продолжается за счёт грейс-запаса ({balance.grace_minutes} мин).
                  Пополните, чтобы кампания не остановилась.
                </p>
              )}

              {!balance.can_place_outbound && (
                <p className="text-sm text-red-700" data-testid="voice-stopped-note">
                  Исходящие звонки не начинаются. Входящие принимаются и будут учтены в следующем
                  периоде.
                </p>
              )}

              {balance.overage_rub && (
                <p className="text-sm text-gray-500">Сверх пакета: {balance.overage_rub} ₽/мин</p>
              )}

              {balance.period_end && (
                <p className="text-sm text-gray-500" data-testid="voice-period-end">
                  {/* Срок обязателен на экране: без него абонентка выглядит
                      разовой покупкой минут, и клиент не ждёт счёта. */}
                  Оплачено до {formatPeriodEnd(balance.period_end)}
                  {daysLeft(balance.period_end) >= 0
                    ? ` · осталось ${daysLeft(balance.period_end)} дн.`
                    : ' · срок истёк'}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">История списаний</CardTitle>
        </CardHeader>
        <CardContent>
          {chargesLoading ? (
            <p className="text-sm text-gray-500">Загружаем историю…</p>
          ) : !charges || charges.results.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="voice-charges-empty">
              Звонков пока не было.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="voice-charges-table">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-4">Звонок</th>
                    <th className="py-2 pr-4 text-right">Длительность</th>
                    <th className="py-2 text-right">Списано</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.results.map((charge) => (
                    <tr key={charge.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <div>{formatDate(charge.started_at)}</div>
                        <div className="text-xs text-gray-500">
                          {charge.direction === 'outbound' ? 'Исходящий' : 'Входящий'}
                          {charge.reason_display ? ` · ${charge.reason_display}` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {formatDuration(charge.duration_seconds)}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {charge.billed_minutes > 0 ? `${charge.billed_minutes} мин` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
